// Default to same-origin (relative) for cookie-based auth.
// Override with REACT_APP_API_BASE_URL for local dev pointing at a different host.
const DEFAULT_BASE_URL = "";
const getBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  return (envUrl && envUrl.trim().replace(/\/$/, "")) || DEFAULT_BASE_URL;
};

export const buildUrl = (path) => `${getBaseUrl()}${path.startsWith("/") ? path : `/${path}`}`;

export const handleResponse = async (response) => {
  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? await response.json().catch(() => ({})) : await response.text();

  if (!response.ok) {
    const message =
      (typeof payload === "object" && payload !== null ? payload.error ?? payload.message : undefined) ||
      (typeof payload === "string" && payload ? payload : undefined) ||
      response.statusText ||
      "Request failed";
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
};

export const register = (email, password, phone) =>
  fetch(buildUrl("/api/auth/register"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, phone }),
    credentials: "include",
  }).then(handleResponse);

export const login = (email, password, rememberMe) =>
  fetch(buildUrl("/api/auth/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, rememberMe: Boolean(rememberMe) }),
    credentials: "include",
  }).then(handleResponse);

export const loginWithGoogle = (credential, rememberMe) =>
  fetch(buildUrl("/api/auth/google"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ credential, rememberMe: Boolean(rememberMe) }),
    credentials: "include",
  }).then(handleResponse);

export const forgotPassword = (email) =>
  fetch(buildUrl("/api/auth/forgot-password"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
    credentials: "include",
  }).then(handleResponse);

export const resetPassword = (token, newPassword) =>
  fetch(buildUrl("/api/auth/reset-password"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, newPassword }),
    credentials: "include",
  }).then(handleResponse);

export const getProfile = () =>
  fetch(buildUrl("/api/profile"), {
    credentials: "include",
  }).then(handleResponse);

export const updateProfile = (payload) =>
  fetch(buildUrl("/api/profile"), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    credentials: "include",
  }).then(handleResponse);

export const changePassword = (payload) =>
  fetch(buildUrl("/api/profile/password"), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    credentials: "include",
  }).then(handleResponse);

export const logout = () =>
  fetch(buildUrl("/api/auth/logout"), {
    method: "POST",
    credentials: "include",
  }).then(handleResponse);

const SESSION_KEY = "wayli.session";
const SESSION_EXPIRY_KEY = "wayli.sessionExpires";

export const authApi = {
  register,
  login,
  loginWithGoogle,
  logout,
  forgotPassword,
  resetPassword,
  getProfile,
  updateProfile,
  changePassword,
};

export const getStoredSession = () =>
  localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY) || null;

export const getSessionExpiry = () =>
  localStorage.getItem(SESSION_EXPIRY_KEY) || sessionStorage.getItem(SESSION_EXPIRY_KEY) || null;

export const storeAuthSession = (expiresUtc, remember) => {
  const primary = remember ? localStorage : sessionStorage;
  const secondary = remember ? sessionStorage : localStorage;

  primary.setItem(SESSION_KEY, "1");
  if (expiresUtc) primary.setItem(SESSION_EXPIRY_KEY, expiresUtc);

  secondary.removeItem(SESSION_KEY);
  secondary.removeItem(SESSION_EXPIRY_KEY);
};

export const clearAuthSession = () => {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(SESSION_EXPIRY_KEY);
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_EXPIRY_KEY);
};

export default authApi;
